import { create } from 'zustand'

/**
 * revenueSyncStore — shared state for the HubSpot → Revenue sync.
 *
 * Multiple surfaces care about sync state:
 *   - useRevenueAutoSync (root) — owns the 10-minute poll loop
 *   - RevenuePillar         — renders "Syncing…" / last-synced chip,
 *                             "Sync now" button
 *
 * Centralizing the status + last-synced timestamp + last-error here
 * means:
 *   (a) the "Sync now" button and the auto-poll can't both run a
 *       sync simultaneously (the store guards via `inFlight`);
 *   (b) the last-synced chip updates in real time whether the sync
 *       was triggered manually or by the poll;
 *   (c) error state surfaces uniformly no matter who fired the call.
 */
export const useRevenueSyncStore = create((set, get) => ({
  // 'idle' | 'syncing' | 'success' | 'error'
  status: 'idle',
  lastSyncedAt: null,   // ISO string — set on successful sync
  lastReceipt: null,    // response payload from /hs/sync-revenue
  error: null,          // last error message, if any
  inFlight: false,      // hard lock against concurrent syncs

  // Called by whoever triggers the sync — auto or manual.
  // Returns a promise that resolves to the receipt (or rejects on error).
  async runSync(syncFn) {
    if (get().inFlight) {
      // Already running — no-op rather than stack a second request.
      return null
    }
    set({ status: 'syncing', error: null, inFlight: true })
    try {
      const receipt = await syncFn()
      set({
        status: 'success',
        lastSyncedAt: receipt?.updatedAt || new Date().toISOString(),
        lastReceipt: receipt,
        error: null,
        inFlight: false,
      })
      return receipt
    } catch (err) {
      set({
        status: 'error',
        error: err?.message || 'Sync failed',
        inFlight: false,
      })
      throw err
    }
  },
}))
